import { JsonSettingsConfig, JsonSettingTypes, JsonSettingsBase } from "@shared/components/json-settings/json-settings.model";
import { inject, Injectable } from "@angular/core";
import { get, merge } from "lodash-es";
import IlhJsonSettingsComponentJson from "@shared/components/json-settings/json-settings-configs/json/ilh-json-settings-component.json";
import GlhJsonSettingsComponentJson from "@shared/components/json-settings/json-settings-configs/json/glh-json-settings-component.json";
import WcJsonSettingsComponentJson from "@shared/components/json-settings/json-settings-configs/json/wc-json-settings-component.json";
import SharedJsonSettingsComponentJson from "@shared/components/json-settings/json-settings-configs/json/shared-json-settings-component.json";
import { ComponentOverridesSettings } from "@admin/application-settings/component-overrides/component-overrides.model";
import { ComponentOverridesService } from "@admin/application-settings/component-overrides/component-overrides.service";
import { EncodeDecodeService } from "@app/core/services/encode-decode.service";
import { FileDownloadService } from "@app/core/services/file-download.service";
import { FileTypes } from "@app/shared/constants/file-types.constants";
import { FileDownload } from "@app/shared/models/file.model";
import { switchMap, of, tap } from "rxjs";
import { RolesService } from "@admin/roles/roles.service";

@Injectable({
  providedIn: "root"
})
export class JsonSettingsService {
  private _defaultJsonSettings: any = {};
  private _componentOverridesService: ComponentOverridesService = inject(ComponentOverridesService);
  private _rolesService: RolesService = inject(RolesService);
  private _fds: FileDownloadService = inject(FileDownloadService);
  private _ecd: EncodeDecodeService = inject(EncodeDecodeService);

  loadDefaults() {
    this._defaultJsonSettings = {
      ...JSON.parse(JSON.stringify(IlhJsonSettingsComponentJson)),
      ...JSON.parse(JSON.stringify(GlhJsonSettingsComponentJson)),
      ...JSON.parse(JSON.stringify(WcJsonSettingsComponentJson)),
      ...JSON.parse(JSON.stringify(SharedJsonSettingsComponentJson))
    };
  }

  /**
   * Merge JSON objects together to set new values
   * @param oldObject
   * @param newObject
   * @returns
   */
  mergeObjects(oldObject: any, newObject: any) {
    return merge(oldObject, newObject);
  }

  /**
   * Filter JSON objects by the active config
   * NOTE: Shared components will be in their own section.
   * @param object
   * @param config
   * @returns
   */
  filterByConfig(object: any, config: JsonSettingsConfig, isTranslation?: boolean) {
    let newJsonObject: any;
    Object.keys(object).some((key) => {
      if (
        config?.path === key ||
        config?.translationPath === key ||
        config?.sharedComponents?.includes(key) ||
        config?.sharedComponentTranslationPaths?.includes(key)
      ) {
        newJsonObject = this._mergeByKey(newJsonObject, object, key);
      }
    });

    newJsonObject = this.sortObjectById(newJsonObject, config, isTranslation);

    return newJsonObject;
  }

  /**
   * Filter JSON objects by the active id
   * @param object
   * @param config
   * @returns
   */
  filterById(object: any, id: string) {
    let newJsonObject: any;
    Object.keys(object).some((key) => {
      if (id === key) {
        newJsonObject = this._mergeByKey(newJsonObject, object, key);
      }
    });

    return newJsonObject;
  }

  /**
   * Pass in path of CategorySection. This will return the entire path except the final value.
   * XXX.XXX.XXX => XXX.XXX
   * @param path
   * @returns
   */
  getFullParentPath(path: string) {
    if (!path) {
      return "";
    }

    return path.substring(0, path.lastIndexOf("."));
  }

  /**
   * Flatten all CS items
   * @param array
   * @param data
   * @returns
   */
  flattenCategorySections(array: JsonSettingsBase[], data: JsonSettingsBase[] = []) {
    for (const a of array.filter((x) => this.isCategorySectionType(x.type))) {
      data.push(a);

      if (a?.valueSettings?.length) {
        this.flattenCategorySections(a.valueSettings, data);
      }
    }

    return data;
  }

  flatten(array: JsonSettingsBase[], data: JsonSettingsBase[] = []) {
    for (const a of array) {
      data.push(a);

      if (a?.valueSettings?.length) {
        this.flatten(a.valueSettings, data);
      }
    }

    return data;
  }

  /**
   * Sort JSON object so the main component is always first.
   * @param object
   * @param config
   * @param isTranslation
   */
  sortObjectById(object: any, config: JsonSettingsConfig, isTranslation: boolean) {
    return Object.keys(object)
      .sort((a) => {
        if (isTranslation) {
          return a === config.translationPath ? -1 : 0;
        } else {
          return a === config.path ? -1 : 0;
        }
      })
      .reduce(
        (_sortedObj, key) => ({
          ..._sortedObj,
          [key]: object[key]
        }),
        {}
      );
  }

  isJson(value: any): boolean {
    return !Array.isArray(value) && typeof value === "object";
  }

  isJsonBooleanType(value: any): boolean {
    return typeof value === "boolean";
  }

  isJsonTextType(value: any): boolean {
    return typeof value === "string";
  }

  isJsonNumericType(value: any): boolean {
    return typeof value === "number";
  }

  isJsonListType(value: any): boolean {
    return Array.isArray(value);
  }

  /**
   * Determine if setting is a section
   * @param questionType
   * @returns
   */
  isSectionType = (type: string): boolean => {
    return type === this.settingTypes.section;
  };

  /**
   * Determine if question type is a category
   * @param questionType
   * @returns
   */
  isCategoryType = (type: string): boolean => {
    return type === this.settingTypes.category;
  };

  /**
   * Determine if question type is a category section
   * @param questionType
   * @returns
   */
  isCategorySectionType = (type: string): boolean => {
    return type === this.settingTypes.category_section;
  };

  /**
   * Determines if the type is a input (string or number)
   * @param question
   * @returns
   */
  isTextType = (type: string): boolean => {
    return type === this.settingTypes.text;
  };

  /**
   * Determines if the type is a input (string or number)
   * @param question
   * @returns
   */
  isNumberType = (type: string): boolean => {
    return type === this.settingTypes.numeric;
  };

  /**
   * Determines if the type is a boolean
   * @param question
   * @returns
   */
  isBooleanType = (type: string): boolean => {
    return type === this.settingTypes.boolean;
  };

  /**
   * Determines if the type is a list
   * @param question
   * @returns
   */
  isListType = (type: string): boolean => {
    return type === this.settingTypes.list;
  };

  /**
   * returns a boolean value based on the passed in object
   * If no option exists, the fallback from the assets config will be returned.
   * @param object
   * @param path
   */
  getBooleanValue(path: string): boolean {
    const value: boolean = this._getJsonSettingsValue(path);
    if (value === undefined) {
      return true;
    }
    return value;
  }

  /**
   * Fetch overrides by role first. If no override exists we will then use the global overrides.
   * If none exist, screen will default to show everything.
   */
  loadComponentOverride(roleId: string, componentId: string, menuId: string) {
    const parms = {
      roleId: {
        value: roleId
      },
      componentId: {
        value: componentId
      },
      menuId: {
        value: menuId
      },
      content: {
        value: ""
      }
    } as ComponentOverridesSettings;

    return this._rolesService.fetchComponentSettingsById(parms).pipe(
      tap((resp) => {
        if (resp) {
          parms.content.value = resp.content.value;
        }
      }),
      switchMap(() => {
        if (!parms.content.value) {
          return this._componentOverridesService.fetchComponentSettingsById(parms);
        }
        return of(parms);
      }),
      switchMap((result) => {
        if (result.content.value) {
          const payload: FileDownload = {
            file: result.content.value as string,
            type: FileTypes.TEXT
          };
          return this._fds.downloadFileAsString(payload, null, false);
        } else {
          return of("");
        }
      }),
      tap((result) => {
        if (result) {
          try {
            this._defaultJsonSettings = merge(this._defaultJsonSettings, JSON.parse(this._ecd.decodeString(result)));
          } catch {}
        }
      })
    );
  }

  get settingTypes() {
    return JsonSettingTypes;
  }

  /**
   * Merge 2 JSON objects based on key value.
   * @param newObject
   * @param oldObject
   * @param key
   * @returns
   */
  private _mergeByKey(newObject: any, oldObject: any, key: string) {
    return merge(newObject, { [key]: oldObject[key] });
  }

  /**
   * Retrieves the value from the stored portal settings file based on a path.
   * @param object
   * @param path
   */
  private _getJsonSettingsValue(path: string) {
    return get(this._defaultJsonSettings, path);
  }
}
